import { FC, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { Box, Button, Tab, Tabs } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { DailyNavExpectedAllocation } from 'api/models/DailyNavExpectedAllocation';
import { FormDialog } from 'components';
import {
  DAILY_NAV_REPORT_PHASES,
  DailyNavReportTab,
  isValidStaffFinalizeMessage,
} from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { captureException } from 'scalarAnalytics/postHog';
import { useFirmDocumentActions } from 'services/hooks/documents';
import { useFinalizeDailyNavDay } from 'services/hooks/firms';
import {
  useDownloadDailyNavFinalizeAttachment,
  useGenerateDailyNavReport,
  useGetDailyNavReport,
  useGetDailyNavReportSource,
  useGetDailyNavValidation,
  useSendDailyNavDay,
} from 'services/hooks/firms/useDailyNavReport';
import { Logger } from 'utilities';
import FinalizeTab from './FinalizeTab';
import PreviewTab from './PreviewTab';
import { ReportDialogProps } from './ReportDialog.props';
import { reportDialogColors as colors, reportDialogTabSx } from './ReportDialog.styles';
import SendTab from './SendTab';
import ValidateTab from './ValidateTab';

const logReportGenerateError = (error: unknown, firmId?: number, date?: string) => {
  Logger.error('Error generating daily nav report', error);
  captureException(error, {
    source: 'daily_nav_report_generate',
    firm_id: firmId,
    date,
  });
};

const ReportDialog: FC<ReportDialogProps> = ({
  open,
  onClose,
  firmId,
  date,
  day,
  initialTab,
  isStaff,
  isLocked = false,
}) => {
  const [tab, setTab] = useState<DailyNavReportTab>('validate');
  const [approverNote, setApproverNote] = useState('');
  const [approverNoteFiles, setApproverNoteFiles] = useState<File[]>([]);
  // What validate said this day would close against, captured when the operator
  // moved past it. Null until then, which is why Finalize refuses.
  const [approvedAllocations, setApprovedAllocations] = useState<DailyNavExpectedAllocation[] | null>(null);
  const previewGenerationRequested = useRef(false);

  const {
    data: validation,
    isFetching: isValidating,
    refetch: refetchValidation,
  } = useGetDailyNavValidation(firmId, date, open);
  const { data: report } = useGetDailyNavReport(firmId, date, open);
  const { data: reportSource, isFetching: isFetchingSource } = useGetDailyNavReportSource(firmId, date, open);
  const {
    data: generatedReport,
    mutateAsync: generateDailyNavReport,
    isPending: isGeneratingReport,
  } = useGenerateDailyNavReport(firmId);
  const { mutateAsync: finalizeDailyNavDay, isPending: isFinalizing } = useFinalizeDailyNavDay(firmId);
  const { mutateAsync: sendDailyNavDay, isPending: isSending } = useSendDailyNavDay(firmId);
  const { mutate: downloadFinalizeAttachment } = useDownloadDailyNavFinalizeAttachment();
  const { downloadDocuments, isDownloadingDocuments } = useFirmDocumentActions();

  const isFinalized = Boolean(day?.is_finalized);
  const isSent = Boolean(day?.is_sent);
  const validationPassed = Boolean(validation?.passed);
  const displayedReport = generatedReport?.date === date ? generatedReport : report;
  const hasGeneratedReport = Boolean(displayedReport?.report_file_id);

  const canPreview = validationPassed || isFinalized || isSent;
  const canSendTab = isFinalized || isSent;

  useEffect(() => {
    if (!open) {
      setApproverNote('');
      setApproverNoteFiles([]);
      previewGenerationRequested.current = false;
      return;
    }
    if (initialTab) {
      setTab(initialTab);
      return;
    }
    setApprovedAllocations(null);
    setTab(isSent || isFinalized ? 'send' : 'validate');
  }, [open, initialTab, isFinalized, isSent]);

  useEffect(() => {
    if (
      open &&
      tab === 'preview' &&
      validationPassed &&
      !isFinalized &&
      !isSent &&
      !isLocked &&
      !previewGenerationRequested.current
    ) {
      previewGenerationRequested.current = true;
      generateDailyNavReport({ date }).catch(error => {
        logReportGenerateError(error, firmId, date);
        previewGenerationRequested.current = false;
      });
    }
  }, [open, tab, validationPassed, isFinalized, isSent, isLocked, generateDailyNavReport, date, firmId]);

  const sourceRows = useMemo(() => reportSource?.rows ?? [], [reportSource?.rows]);
  const artifactColumns = useMemo(() => displayedReport?.columns ?? [], [displayedReport?.columns]);
  const artifactRows = useMemo(() => displayedReport?.rows ?? [], [displayedReport?.rows]);
  const recipients = day?.recipients?.length ? day.recipients : [];

  const canConfirmFinalize =
    validationPassed &&
    hasGeneratedReport &&
    !isFinalized &&
    !isLocked &&
    (!isStaff || isValidStaffFinalizeMessage(approverNote));

  const handleFinalize = async () => {
    if (!canConfirmFinalize) {
      return;
    }
    // The snapshot taken when the operator left the validate tab, NOT the live
    // query: the server refuses if its own resolution differs, so the day
    // commits what was reviewed. Never rebuild this client-side.
    if (!approvedAllocations) {
      enqueueSnackbar('Re-run validation before finalizing.', { variant: 'warning' });
      return;
    }
    await finalizeDailyNavDay({
      date,
      finalize_approver_note: isStaff ? approverNote.trim() : undefined,
      files: isStaff ? approverNoteFiles : undefined,
      expected_allocations: approvedAllocations,
    });
    setApproverNoteFiles([]);
    setTab('send');
  };

  const handleSend = async () => {
    await sendDailyNavDay({ date });
  };

  // The tab strip and the footer's Back/Next both come through here, so neither can
  // skip the allocation freeze below or the preview generation.
  const goToTab = async (value: DailyNavReportTab) => {
    previewGenerationRequested.current = value === 'preview';
    // Freeze the allocations the operator is moving forward with. The validate
    // query has staleTime 0 and refetches on window focus, so reading it live
    // at click time would let an alt-tab silently replace the token with the
    // server's CURRENT answer -- defeating the guard it exists to be.
    if (tab === 'validate' && value !== 'validate' && validation?.expected_allocations) {
      setApprovedAllocations(validation.expected_allocations);
    }
    setTab(value);
    if (value === 'preview' && validationPassed && !isFinalized && !isSent && !isLocked) {
      try {
        await generateDailyNavReport({ date });
      } catch (error) {
        logReportGenerateError(error, firmId, date);
      }
    }
  };

  const handleTabChange = (_event: SyntheticEvent, value: DailyNavReportTab) => goToTab(value);

  const canDownloadReport = Boolean(firmId && displayedReport?.report_file_id);
  const handleDownload = () => {
    if (!firmId || !displayedReport?.report_file_id) {
      return;
    }
    downloadDocuments({
      documents: [
        {
          fileId: displayedReport.report_file_id,
          fileName: displayedReport.filename || `daily-nav-${date ?? 'report'}.csv`,
          firmId,
        },
      ],
    }).catch(() => undefined);
  };

  const isTabEnabled = (phaseTab: DailyNavReportTab) => {
    if (phaseTab === 'validate') {
      return true;
    }
    if (phaseTab === 'send') {
      return canSendTab;
    }
    if (phaseTab === 'finalize') {
      return (hasGeneratedReport && validationPassed) || isFinalized || isSent;
    }
    return canPreview;
  };

  // Back/Next walk the phase order, not the enabled subset: a phase the operator
  // cannot enter yet is a stop, matching the tab strip, not something to step over.
  const phaseIndex = DAILY_NAV_REPORT_PHASES.findIndex(phase => phase.tab === tab);
  const previousTab = phaseIndex > 0 ? DAILY_NAV_REPORT_PHASES[phaseIndex - 1].tab : undefined;
  const nextTab =
    phaseIndex >= 0 && phaseIndex < DAILY_NAV_REPORT_PHASES.length - 1
      ? DAILY_NAV_REPORT_PHASES[phaseIndex + 1].tab
      : undefined;

  let footerButton;
  if (tab === 'validate') {
    footerButton = (
      <Button
        variant="outlined"
        color="inherit"
        onClick={() => refetchValidation()}
        disabled={isValidating}
        data-testid="daily-nav-rerun-validations"
        sx={{
          borderColor: colors.border,
          color: colors.ink,
          textTransform: 'none',
        }}>
        Re-run validations
      </Button>
    );
  } else if (tab === 'preview' && !isFinalized && !isSent && !isLocked) {
    footerButton = (
      <Button
        variant="outlined"
        color="inherit"
        onClick={() =>
          generateDailyNavReport({ date }).catch(error => {
            logReportGenerateError(error, firmId, date);
          })
        }
        disabled={!validationPassed || isGeneratingReport}
        data-testid="daily-nav-regenerate-report"
        sx={{ borderColor: colors.border, color: colors.ink, textTransform: 'none' }}>
        Regenerate preview
      </Button>
    );
  }

  const footerButtons = (
    <Box display="flex" alignItems="center" gap={1}>
      {footerButton}
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<ChevronLeft />}
        onClick={() => {
          if (previousTab) {
            goToTab(previousTab);
          }
        }}
        disabled={!previousTab || !isTabEnabled(previousTab)}
        data-testid="daily-nav-report-back"
        sx={{ borderColor: colors.border, color: colors.ink, textTransform: 'none' }}>
        Back
      </Button>
      <Button
        variant="outlined"
        color="inherit"
        endIcon={<ChevronRight />}
        onClick={() => {
          if (nextTab) {
            goToTab(nextTab);
          }
        }}
        disabled={!nextTab || !isTabEnabled(nextTab)}
        data-testid="daily-nav-report-next"
        sx={{ borderColor: colors.border, color: colors.ink, textTransform: 'none' }}>
        Next
      </Button>
    </Box>
  );

  return (
    <FormDialog
      title="Prepare Report"
      open={open}
      editMode
      isValid
      isLoading={
        isFinalizing ||
        isSending ||
        isGeneratingReport ||
        (tab === 'validate' && (isValidating || isFetchingSource))
      }
      showSaveButton={false}
      showCancelButton={false}
      disableTitleClose={false}
      bigDialog
      onClose={onClose}
      customButton={footerButtons}>
      <Box>
        <Tabs value={tab} onChange={handleTabChange} sx={reportDialogTabSx} data-testid="daily-nav-report-tabs">
          {DAILY_NAV_REPORT_PHASES.map(phase => (
            <Tab
              key={phase.tab}
              value={phase.tab}
              label={phase.label}
              disabled={!isTabEnabled(phase.tab)}
              data-testid={`daily-nav-report-tab-${phase.tab}`}
            />
          ))}
        </Tabs>

        {tab === 'validate' && <ValidateTab checks={validation?.checks} sourceRows={sourceRows} />}

        {tab === 'preview' && (
          <PreviewTab hasGeneratedReport={hasGeneratedReport} columns={artifactColumns} rows={artifactRows} />
        )}

        {tab === 'finalize' && (
          <FinalizeTab
            columns={artifactColumns}
            rows={artifactRows}
            day={day}
            isFinalized={isFinalized}
            isStaff={isStaff}
            isLocked={isLocked}
            validationPassed={validationPassed}
            hasGeneratedReport={hasGeneratedReport}
            approverNote={approverNote}
            onApproverNoteChange={setApproverNote}
            approverNoteFiles={approverNoteFiles}
            onApproverNoteFilesChange={setApproverNoteFiles}
            canConfirmFinalize={canConfirmFinalize}
            isFinalizing={isFinalizing}
            onFinalize={handleFinalize}
            canDownloadReport={canDownloadReport}
            isDownloadingDocuments={isDownloadingDocuments}
            onDownload={handleDownload}
            onDownloadAttachment={downloadFinalizeAttachment}
          />
        )}

        {tab === 'send' && (
          <SendTab
            columns={artifactColumns}
            rows={artifactRows}
            day={day}
            recipients={recipients}
            isSent={isSent}
            isFinalized={isFinalized}
            isSending={isSending}
            onSend={handleSend}
            canDownloadReport={canDownloadReport}
            isDownloadingDocuments={isDownloadingDocuments}
            onDownload={handleDownload}
            onDownloadAttachment={downloadFinalizeAttachment}
          />
        )}
      </Box>
    </FormDialog>
  );
};

export default ReportDialog;
